import { LogoIcon } from './Logo';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconTruck, IconCheckCircle, IconRotateCcw, IconMedal, IconStore, IconMail, IconX, IconMusic } from './Icons';
import { API_BASE_URL } from './config';

const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 6l6 6-6 6" />
  </svg>
);

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const TrendingUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
  </svg>
);
const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const StoreIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l1.5-5h15L21 9"/><path d="M3 9h18v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/><path d="M9 20v-6h6v6"/>
  </svg>
);
const MailGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);

const SELLER_FEATURES = [
  { Icon: UsersIcon, cls: 'blue', titleKey: 'footer.sellerFeatureReachMoreTitle', subKey: 'footer.sellerFeatureReachMoreSub' },
  { Icon: TrendingUpIcon, cls: 'positive', titleKey: 'footer.sellerFeatureGrowFasterTitle', subKey: 'footer.sellerFeatureGrowFasterSub' },
  { Icon: ShieldIcon, cls: 'info', titleKey: 'footer.sellerFeatureTrustedTitle', subKey: 'footer.sellerFeatureTrustedSub' },
];

// Renders a link entry as a real <a href> when it has a known destination,
// and as a plain button otherwise (placeholder entries with no page yet, and
// flows that need a handler rather than a URL, like opening the sitemap XML).
//
// The footer appears on every page, so these are the site's most repeated
// internal links -- as <button onClick> they gave crawlers no path to the
// legal, company and support pages at all. `href` is only set for pages that
// are actually indexable: the account/order-tracking entries stay buttons
// precisely because those pages are noindex, and site-wide links pointing at
// them would just bleed crawl budget.
function FooterLinkCol({ title, links, onClick, t }) {
  return (
    <div className="ftr-col">
      <h4 className="ftr-col-title">{title}</h4>
      <ul className="ftr-link-list">
        {links.map(link => (
          <li key={link.key}>
            {link.href ? (
              // onClick is kept alongside the href: several handlers do more
              // than navigate (resetting category/sort state, closing panels),
              // and Link handles the actual navigation plus modified-clicks.
              <Link className="ftr-link" to={link.href} onClick={() => onClick(link.key)}>
                <span>{t(link.labelKey)}</span>
                <Chevron />
              </Link>
            ) : (
              <button className="ftr-link" onClick={() => onClick(link.key)}>
                <span>{t(link.labelKey)}</span>
                <Chevron />
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Stable, untranslated ids used for click routing -- the displayed label
// is translated separately via labelKey, so switching languages can never
// break a handler's `if (key === '...')` comparisons.
// `href` marks entries that point at a real, indexable page -- see
// FooterLinkCol for why only those become anchors. Entries without one are
// either placeholders with no page yet, or deliberately-noindex destinations.
const SHOPPING_LINKS = [
  { key: 'all-categories',    labelKey: 'footer.linkAllCategories', href: '/' },
  // new-arrivals / trending / top-rated all land on the storefront with a
  // different sort applied, which isn't reflected in the URL -- they'd emit
  // three more identical "/" links, so they stay handler-only.
  { key: 'new-arrivals',      labelKey: 'footer.linkNewArrivals' },
  { key: 'best-deals',        labelKey: 'footer.linkBestDeals', href: '/deals' },
  { key: 'flash-sales',       labelKey: 'footer.linkFlashSales', href: '/deals' },
  { key: 'trending-products', labelKey: 'footer.linkTrendingProducts' },
  { key: 'top-rated',         labelKey: 'footer.linkTopRatedProducts' },
  { key: 'brands',            labelKey: 'footer.linkBrands', href: '/brands' },
];
const SELLERS_LINKS = [
  { key: 'become-seller',     labelKey: 'footer.becomeSeller', href: '/become-a-seller' },
  { key: 'seller-dashboard',  labelKey: 'footer.linkSellerDashboard' },
  { key: 'seller-guidelines', labelKey: 'footer.linkSellerGuidelines', href: '/seller-guidelines' },
  { key: 'commission-plans',  labelKey: 'footer.linkCommissionPlans', href: '/commission-plan' },
  { key: 'seller-support',    labelKey: 'footer.linkSellerSupport', href: '/contact' },
  { key: 'seller-faqs',       labelKey: 'footer.linkSellerFaqs', href: '/seller-faqs' },
];
const CUSTOMER_SERVICE_LINKS = [
  { key: 'contact-us',       labelKey: 'footer.linkContactUs', href: '/contact' },
  { key: 'help-center',      labelKey: 'footer.linkHelpCenter', href: '/help-center' },
  { key: 'return-policy',    labelKey: 'footer.linkReturnPolicy', href: '/returns-refunds' },
  { key: 'refund-policy',    labelKey: 'footer.linkRefundPolicy', href: '/returns-refunds' },
  { key: 'shipping-policy',  labelKey: 'footer.linkShippingPolicy', href: '/shipping-policy' },
  // /track is noindex (order-specific, auth-gated) -- no sitewide link to it.
  { key: 'track-your-order', labelKey: 'footer.linkTrackYourOrder' },
  { key: 'faq',              labelKey: 'footer.linkFaq', href: '/faq' },
];
const COMPANY_LINKS = [
  { key: 'about-acymart',      labelKey: 'footer.linkAboutAcymart', href: '/about' },
  { key: 'get-the-app',        labelKey: 'footer.linkGetTheApp', href: '/app' },
  { key: 'careers',            labelKey: 'footer.linkCareers', href: '/careers' },
  { key: 'blog',               labelKey: 'footer.linkBlog', href: '/news-updates' },
  { key: 'terms-conditions',   labelKey: 'footer.linkTermsConditions', href: '/terms-conditions' },
  { key: 'privacy-policy',     labelKey: 'footer.linkPrivacyPolicy', href: '/privacy-policy' },
  { key: 'cookie-policy',      labelKey: 'footer.linkCookiePolicy', href: '/cookie-policy' },
  { key: 'news-updates',       labelKey: 'footer.linkNewsUpdates', href: '/news-updates' },
];
const MY_ACCOUNT_LINKS = [
  { key: 'my-orders',         labelKey: 'footer.linkMyOrders' },
  { key: 'wishlist',          labelKey: 'footer.linkWishlist' },
  { key: 'compare-products',  labelKey: 'footer.linkCompareProducts' },
  { key: 'account-settings',  labelKey: 'footer.linkAccountSettings' },
  { key: 'addresses',         labelKey: 'footer.linkAddresses' },
  { key: 'payment-methods',   labelKey: 'footer.linkPaymentMethods' },
];
const BOTTOM_LINKS = [
  // 'sitemap' opens the generated sitemap.xml in a new tab rather than
  // navigating in-app, so it stays a button with its own handler.
  { key: 'sitemap',          labelKey: 'footer.linkSitemap' },
  { key: 'privacy-policy',   labelKey: 'footer.linkPrivacyPolicy', href: '/privacy-policy' },
  { key: 'terms-conditions', labelKey: 'footer.linkTermsConditions', href: '/terms-conditions' },
  { key: 'cookie-policy',    labelKey: 'footer.linkCookiePolicy', href: '/cookie-policy' },
];

const TRUST_ITEMS = [
  // Was "Free Delivery / On orders over $50". No such threshold exists, so the
  // badge states what every order genuinely offers instead.
  { Icon: IconTruck, cls: 'blue', titleKey: 'footer.trustCodTitle', subKey: 'footer.trustCodSub' },
  { Icon: IconCheckCircle, cls: 'positive', titleKey: 'footer.trustSecurePaymentTitle', subKey: 'footer.trustSecurePaymentSub' },
  { Icon: IconRotateCcw, cls: 'orange', titleKey: 'footer.trustEasyReturnsTitle', subKey: 'footer.trustEasyReturnsSub' },
  { Icon: IconMedal, cls: 'info', titleKey: 'footer.trustVerifiedSellersTitle', subKey: 'footer.trustVerifiedSellersSub' },
];

const SOCIAL_LINKS = [
  { label: 'Facebook', icon: 'f', cls: 'fb', url: 'https://www.facebook.com/profile.php?id=61591574926628' },
  { label: 'Instagram', icon: '◎', cls: 'ig' },
  { label: 'X', icon: <IconX size={14} />, cls: 'x' },
  { label: 'TikTok', icon: <IconMusic size={14} />, cls: 'tt' },
  { label: 'YouTube', icon: '▶', cls: 'yt' },
  { label: 'LinkedIn', icon: 'in', cls: 'li' },
];

export default function Footer({ onShopAll, onShopNew, onShopDeals, onShopTrending, onShopTopRated, onSitemap, onBecomeSeller, onMyOrders, onWishlist, onAddresses, onSettings, onTrackOrder, onPlaceholder, onPrivacy, onTerms, onCookies, onReturns, onShipping, onCareers, onAbout, onCommissionPlan, onSellerGuidelines, onNewsUpdates, onBrands, onContact, onFaq, onHelpCenter, onSellerFaq, onGetApp }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || subscribing) return;
    setSubscribing(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        onPlaceholder(data.error || t('footer.subscribeError'), 'error');
      } else {
        onPlaceholder(t('footer.subscribeSuccess', { email: email.trim() }), 'success');
        setEmail('');
      }
    } catch {
      onPlaceholder(t('footer.networkError'), 'error');
    }
    setSubscribing(false);
  };

  const notAvailable = (key, links) =>
    onPlaceholder(t('footer.notAvailableYet', { label: t(links.find(l => l.key === key)?.labelKey || key) }));

  const handleShoppingClick = (key) => {
    if (key === 'all-categories') return onShopAll();
    if (key === 'new-arrivals') return onShopNew();
    if (key === 'best-deals' || key === 'flash-sales') return onShopDeals();
    if (key === 'trending-products') return onShopTrending();
    if (key === 'top-rated') return onShopTopRated();
    if (key === 'brands') return onBrands();
    return notAvailable(key, SHOPPING_LINKS);
  };

  const handleSellersClick = (key) => {
    if (key === 'become-seller' || key === 'seller-dashboard') return onBecomeSeller();
    if (key === 'seller-faqs') return onSellerFaq();
    if (key === 'commission-plans') return onCommissionPlan();
    if (key === 'seller-guidelines') return onSellerGuidelines();
    if (key === 'seller-support') return onContact();
    return notAvailable(key, SELLERS_LINKS);
  };

  const handleAccountClick = (key) => {
    if (key === 'my-orders') return onMyOrders();
    if (key === 'wishlist') return onWishlist();
    if (key === 'addresses') return onAddresses();
    if (key === 'account-settings') return onSettings();
    if (key === 'payment-methods') return onSettings();
    if (key === 'compare-products') return onShopAll();
    return notAvailable(key, MY_ACCOUNT_LINKS);
  };

  const handleCustomerServiceClick = (key) => {
    if (key === 'track-your-order') return onTrackOrder();
    if (key === 'return-policy' || key === 'refund-policy') return onReturns();
    if (key === 'shipping-policy') return onShipping();
    if (key === 'contact-us') return onContact();
    if (key === 'help-center') return onHelpCenter();
    if (key === 'faq') return onFaq();
    return notAvailable(key, CUSTOMER_SERVICE_LINKS);
  };

  const handleCompanyClick = (key) => {
    if (key === 'terms-conditions') return onTerms();
    if (key === 'privacy-policy') return onPrivacy();
    if (key === 'cookie-policy') return onCookies();
    if (key === 'careers') return onCareers();
    if (key === 'about-acymart') return onAbout();
    if (key === 'get-the-app') return onGetApp();
    if (key === 'news-updates') return onNewsUpdates();
    if (key === 'blog') return onNewsUpdates();
    return notAvailable(key, COMPANY_LINKS);
  };

  const handleBottomLinkClick = (key) => {
    if (key === 'privacy-policy') return onPrivacy();
    if (key === 'terms-conditions') return onTerms();
    if (key === 'cookie-policy') return onCookies();
    if (key === 'sitemap') return onSitemap();
    return notAvailable(key, BOTTOM_LINKS);
  };

  return (
    <footer className="ftr">
      {/* Trust badges */}
      <div className="ftr-trust-bar">
        {TRUST_ITEMS.map(item => (
          <div className="ftr-trust-item" key={item.titleKey}>
            <span className={`ftr-trust-icon ftr-trust-icon--${item.cls}`}><item.Icon /></span>
            <div>
              <div className="ftr-trust-title">{t(item.titleKey)}</div>
              <div className="ftr-trust-sub">{t(item.subKey)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA row */}
      <div className="ftr-cta-row">
        <div className="ftr-brand-block">
          <button className="ftr-brand-logo" onClick={onShopAll}>
            <LogoIcon size={34} />
            <span className="ftr-brand-name">Acy<span className="logo-store-text">Mart</span></span>
          </button>
          <p className="ftr-brand-tag">{t('footer.brandTagline')}</p>
          <p className="ftr-brand-desc">{t('footer.brandDescription')}</p>
        </div>

        <div className="ftr-cta-block ftr-cta-block--seller">
          <div className="ftr-cta-top">
            <span className="ftr-cta-icon"><IconStore /></span>
            <div>
              <div className="ftr-cta-title">{t('footer.startSellingTitle')}</div>
              <p className="ftr-cta-sub">{t('footer.startSellingSub')}</p>
            </div>
          </div>

          <div className="ftr-cta-features">
            {SELLER_FEATURES.map(({ Icon, cls, titleKey, subKey }) => (
              <div className="ftr-cta-feature" key={titleKey}>
                <span className={`ftr-cta-feature-icon ftr-cta-feature-icon--${cls}`}><Icon /></span>
                <div>
                  <div className="ftr-cta-feature-title">{t(titleKey)}</div>
                  <div className="ftr-cta-feature-sub">{t(subKey)}</div>
                </div>
              </div>
            ))}
          </div>

          <button className="btn ftr-cta-btn" onClick={onBecomeSeller}>
            <StoreIcon /> {t('footer.becomeSeller')} <Chevron />
          </button>
        </div>

        <div className="ftr-cta-block ftr-cta-block--subscribe">
          <div className="ftr-cta-top">
            <span className="ftr-cta-icon ftr-cta-icon--info"><IconMail /></span>
            <div>
              <div className="ftr-cta-title">{t('footer.getExclusiveDealsTitle')}</div>
              <p className="ftr-cta-sub">{t('footer.getExclusiveDealsSub')}</p>
            </div>
          </div>

          <form className="ftr-subscribe-row" onSubmit={handleSubscribe}>
            <div className="ftr-subscribe-input-wrap">
              <span className="ftr-subscribe-input-icon"><MailGlyph /></span>
              <input
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                value={email}
                onChange={e => setEmail(e.target.value)}
                aria-label={t('footer.emailAriaLabel')}
              />
            </div>
            <button type="submit" className="ftr-subscribe-btn" disabled={subscribing}>{subscribing ? t('footer.subscribing') : t('footer.subscribe')}</button>
          </form>
        </div>
      </div>

      {/* Link columns */}
      <div className="ftr-links-row">
        <FooterLinkCol
          title={t('footer.colShopping')}
          links={SHOPPING_LINKS}
          onClick={handleShoppingClick}
          t={t}
        />
        <FooterLinkCol
          title={t('footer.colSellers')}
          links={SELLERS_LINKS}
          onClick={handleSellersClick}
          t={t}
        />
        <FooterLinkCol
          title={t('footer.colCustomerService')}
          links={CUSTOMER_SERVICE_LINKS}
          onClick={handleCustomerServiceClick}
          t={t}
        />
        <FooterLinkCol
          title={t('footer.colCompany')}
          links={COMPANY_LINKS}
          onClick={handleCompanyClick}
          t={t}
        />
        <FooterLinkCol
          title={t('footer.colMyAccount')}
          links={MY_ACCOUNT_LINKS}
          onClick={handleAccountClick}
          t={t}
        />
      </div>

      {/* Social row */}
      <div className="ftr-meta-row">
        <div className="ftr-meta-block">
          <h5 className="ftr-meta-title">{t('footer.followUs')}</h5>
          <div className="ftr-social-row">
            {SOCIAL_LINKS.map(s => (
              s.url ? (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`ftr-social-btn ftr-social-btn--${s.cls}`}
                  aria-label={s.label}
                >
                  {s.icon}
                </a>
              ) : (
                <button
                  key={s.label}
                  className={`ftr-social-btn ftr-social-btn--${s.cls}`}
                  aria-label={s.label}
                  onClick={() => onPlaceholder(t('footer.followUsOn', { platform: s.label }))}
                >
                  {s.icon}
                </button>
              )
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="ftr-bottom-bar">
        <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
        <div className="ftr-bottom-links">
          {BOTTOM_LINKS.map((link, i) => (
            <span key={link.key}>
              {i > 0 && <span className="ftr-bottom-sep">|</span>}
              {link.href
                ? <Link className="ftr-bottom-link" to={link.href}>{t(link.labelKey)}</Link>
                : <button className="ftr-bottom-link" onClick={() => handleBottomLinkClick(link.key)}>{t(link.labelKey)}</button>}
            </span>
          ))}
        </div>
      </div>
    </footer>
  );
}
